import React, { useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { Box, Button, Theme, Typography } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
// eslint-disable-next-line no-duplicate-imports
import makeStyles from "@mui/styles/makeStyles";
// eslint-disable-next-line no-duplicate-imports
import createStyles from "@mui/styles/createStyles";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import { CSSProperties } from "@mui/styles";
import clsx from "clsx";
import { useFormik } from "formik";
import { parse } from "query-string";
import { object, SchemaOf, string } from "yup";
import { setPasswordAction } from "src/app-redux/auth/actions/authActions";
import { selectAuthError, selectStatus } from "src/app-redux/auth/authSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import NJSLogo from "src/assets/images/tfg_mainlogo.svg";
import { AuthRoute } from "../auth.routes";

const mediumText: CSSProperties = {
  fontWeight: 500,
} as const;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footNote: {
      display: "block",
      maxWidth: "320px",
      margin: "auto",
      fontSize: "12px",
      color: "#96A4B9",
    },
    forgotPasswordButton: {
      paddingTop: "0",
      fontSize: "12px",
      color: "#488CFB",
      textTransform: "capitalize",
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
    },
    backdrop: {
      zIndex: theme.zIndex.modal + 1,
      color: "#fff",
    },
    headerLogo: {
      textAlign: "center",
    },
    headerLogoLink: {
      textAlign: "center",
      width: "100%",
      float: "left",
    },
  })
);

interface IForm {
  repeatPassword: string;
  password: string;
}
const validationSchema: SchemaOf<IForm> = object({
  password: string()
    .min(8, "Password should be of minimum 8 characters length")
    .max(256, "Password should not exceed more than 256 characters")
    .required("Password is required"),
  repeatPassword: string()
    .min(8, "Password should be of minimum 8 characters length")
    .max(256, "Password should not exceed more than 256 characters")
    .required("Password is required")
    .test("passwords-match", "Passwords must match", function (value) {
      return this.parent.password === value;
    }),
});
export function SetPasswordPage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const authStatus = useAppSelector(selectStatus);
  const authError = useAppSelector(selectAuthError);
  const resetToken = (parse(location.search).token as string)?.trim();

  const [values, setValues] = useState({
    showPassword: false,
    showRepeatPassword: false,
  });

  const [alert1, setAlert] = useState(Boolean(authStatus === "failed" && authError));
  useEffect(() => {
    if (authStatus === "failed" && authError) setAlert(true);
    if (!authError) setAlert(false);
  }, [authStatus, authError]);
  const handleClose = (event?: Event | React.SyntheticEvent<any, Event>, reason?: string) => {
    if (reason === "clickaway") return;
    setAlert(false);
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };
  const handleClickShowRepeatPassword = () => {
    setValues({ ...values, showRepeatPassword: !values.showRepeatPassword });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const formik = useFormik<IForm>({
    initialValues: {
      repeatPassword: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await dispatch(setPasswordAction({ token: resetToken, password: values.password }));
    },
  });
  return authStatus === "PasswordResetSuccessfull" ? (
    <Redirect to={{ pathname: AuthRoute.path }} />
  ) : (
    <>
      <Backdrop className={classes.backdrop} open={Boolean(authStatus === "loading")}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={alert1}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          Invalid credentials. Please try again.
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
      <div className="login-page">
        <div className="left-section">
          <div className="inner">
            <Box my={2} className="headerLogo" textAlign="center">
              <a href="/" className="headerLogoLink">
                <img src={NJSLogo} alt="logo" width="200" />
              </a>
            </Box>
            <div className="form-wrapper">
              <h2 className="form-heading">Set Password</h2>
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Password"
                  name="password"
                  type={values.showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {values.showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box m={4} />
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Repeat Password"
                  name="repeatPassword"
                  type={values.showRepeatPassword ? "text" : "password"}
                  value={formik.values.repeatPassword}
                  onChange={formik.handleChange}
                  error={formik.touched.repeatPassword && Boolean(formik.errors.repeatPassword)}
                  helperText={formik.touched.repeatPassword && formik.errors.repeatPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowRepeatPassword}
                          //onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {values.showRepeatPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box m={4} />

                <Button type="submit" variant="contained" color="secondary" fullWidth>
                  Set Password
                </Button>
              </form>
            </div>
            <Typography
              component="span"
              variant="body1"
              color="textPrimary"
              style={mediumText}
              className={clsx(classes.footNote)}
            >
              Copyright©, The Facilities Group,{new Date().getFullYear()}
            </Typography>
          </div>
        </div>
        <div className="right-section">
          <div className="login-bg-pattern"></div>
        </div>
      </div>
    </>
  );
}
